import { useState, useEffect, useMemo } from "react";
import { timezoneToCountryCode } from '../../../utils/timezoneToCountry';
import CountryFlag from '@/components/common/CountryFlag';
import { Clock, User, Calendar, ArrowRight } from "lucide-react";
import { usePsychologistContext } from '../../../contexts/PsychologistContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CancellationModalProps, CancellationMotive, InitiatedBy } from "./types";
import { isImpersonated } from "../../../hooks/api/auth/tokenManager";
import apiCoreAdapter from "../../../hooks/apiCoreAdapter";

type Step = 1 | 2;

export function VariantWizard({
  isOpen,
  onClose,
  onConfirm,
  session,
  patient,
}: CancellationModalProps) {
  const [step, setStep] = useState<Step>(1);
  // La cancelación siempre la inicia la psicóloga; si el motivo es "el paciente me pidió cancelar"
  // (PATIENT_ASKED), effectiveInitiatedBy lo mapea a 'Paciente' al enviar.
  const [initiatedBy] = useState<InitiatedBy>('Psicóloga');
  const [selectedMotive, setSelectedMotive] = useState<CancellationMotive | null>(null);
  const [secondaryMotive, setSecondaryMotive] = useState<CancellationMotive | null>(null);
  const [note, setNote] = useState("");
  const [motives, setMotives] = useState<CancellationMotive[]>([]);
  const { psychologist } = usePsychologistContext();

  const impersonated = isImpersonated();

  useEffect(() => {
    if (isOpen) {
      apiCoreAdapter.getCancellationMotives().then(setMotives).catch(() => setMotives([]));
    }
  }, [isOpen]);

  const patientMotives = motives.filter(
    (m) => m.applies_to === 'patient' || m.applies_to === 'both'
  );
  const psychologistMotives = motives.filter(
    (m) => m.applies_to === 'psychologist' || m.applies_to === 'both'
  );

  const primaryMotives = (() => {
    const list = initiatedBy === 'Paciente'
      ? patientMotives.filter((m) => impersonated || m.action !== 'show_secondary')
      : psychologistMotives;
    const weight = (name: string) => {
      if (initiatedBy === 'Paciente') {
        if (name === 'PSYCHOLOGIST_ASKED') return 2;
      } else {
        if (name === 'OTHER') return 2;
        if (name === 'PATIENT_ASKED') return 1;
      }
      return 0;
    };
    return [...list].sort((a, b) => weight(a.name) - weight(b.name));
  })();

  const isConditional = selectedMotive?.action === 'show_secondary';

  const secondaryMotives = (
    selectedMotive?.name === 'PSYCHOLOGIST_ASKED'
      ? psychologistMotives.filter((m) => m.action !== 'show_secondary')
      : patientMotives.filter((m) => m.action !== 'show_secondary')
  ).sort((a, b) => {
    if (a.name === 'OTHER') return 1;
    if (b.name === 'OTHER') return -1;
    return 0;
  });

  const effectiveInitiatedBy: InitiatedBy =
    selectedMotive?.name === 'PSYCHOLOGIST_ASKED' ? 'Psicóloga' :
    selectedMotive?.name === 'PATIENT_ASKED' ? 'Paciente' :
    initiatedBy;

  /** Código ISO2 derivado del timezone IANA del paciente (null si no disponible) */
  const patientIso2 = useMemo(() => {
    if (!session.patientTimezone) return null;
    return timezoneToCountryCode(session.patientTimezone);
  }, [session.patientTimezone]);

  const activeMotive = isConditional ? secondaryMotive : selectedMotive;
  const canConfirm = !!activeMotive && note.trim().length > 0;

  const resetState = () => {
    setStep(1);
    setSelectedMotive(null);
    setSecondaryMotive(null);
    setNote("");
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleConfirm = () => {
    if (!activeMotive) return;
    const initiatedByApi = effectiveInitiatedBy === 'Psicóloga' ? 'psychologist' : 'patient';
    onConfirm(activeMotive.id, note || undefined, initiatedByApi as InitiatedBy);
    resetState();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="sm:max-w-[480px] p-0 gap-0 rounded-2xl bg-white border-none shadow-xl">
        {/* Progress bar */}
        <div className="flex gap-3 px-6 pt-6">
          {[0, 1].map((i) => (
            <div
              key={i}
              className={`h-[3px] flex-1 rounded-full transition-colors duration-300 ${step > i ? 'bg-primary' : 'bg-muted'}`}
            />
          ))}
        </div>

        {/* Step 1 — Confirm cancel */}
        {step === 1 && (
          <>
            <DialogHeader className="px-6 pt-5 pb-0 space-y-1">
              <DialogTitle className="text-xl font-semibold text-foreground">
                ¿Cancelar esta sesión?
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                Podrás volver a agendar más adelante si lo necesitás.
              </DialogDescription>
            </DialogHeader>

            <div className="px-6 py-5">
              <div className="p-5 rounded-xl border-2 border-primary/30 bg-white">
                <p className="text-xs text-muted-foreground mb-1">Confirmar cancelación</p>
                <div className="flex items-center justify-between">
                  <p className="text-xl font-semibold text-foreground">{session.date}</p>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border border-primary/30 text-xs font-medium text-primary">
                    <Clock className="w-3.5 h-3.5" />
                    {session.durationMinutes} minutos
                  </span>
                </div>
                <div className="flex mt-4 border-t border-border/50 pt-4">
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-0.5">Tu horario</p>
                    <p className="text-sm font-medium text-foreground flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                      <span className="whitespace-nowrap">
                        {session.endTime ? `${session.time} – ${session.endTime}` : session.time}
                      </span>
                      <span data-testid="variant-wizard-therapist-flag" aria-hidden="true">
                        <CountryFlag timezone={psychologist?.timezone} fallbackIso2="AR" size="0.875rem" />
                      </span>
                    </p>
                  </div>
                  <div className="w-px bg-border/50 mx-4" />
                  <div className="flex-1">
                    <p className="text-xs text-muted-foreground mb-0.5">Horario Paciente</p>
                    <p className="text-sm font-medium text-foreground">
                      {session.patientTime || (session.endTime ? `${session.time} – ${session.endTime}` : session.time)}
                    </p>
                    <p className="text-xs text-muted-foreground" data-testid="patient-tz-label">
                      ({patientIso2 && <><CountryFlag iso2={patientIso2} size="0.75rem" /> </>}{session.patientTimezoneLabel || 'Argentina'})
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <User className="w-4 h-4" />
                    <span>Paciente</span>
                  </div>
                  <span className="text-sm font-medium text-foreground">{patient.name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span>Tipo de cita</span>
                  </div>
                  <span className="text-sm font-medium text-foreground">{session.therapyType}</span>
                </div>
              </div>
            </div>

            <div className="flex gap-3 px-6 pb-6">
              <Button
                variant="outline"
                className="flex-1 rounded-full border-border hover:bg-muted/50"
                onClick={handleClose}
              >
                Atrás
              </Button>
              <Button
                className="flex-1 rounded-full bg-primary hover:bg-primary/90"
                onClick={() => setStep(2)}
              >
                Sí, cancelar
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </>
        )}

        {/* Step 2 — Who cancelled + reason */}
        {step === 2 && (
          <>
            <DialogHeader className="px-6 pt-5 pb-0 space-y-1">
              <DialogTitle className="text-xl font-semibold text-foreground">
                Motivo de cancelación
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                Seleccioná el motivo de cancelación.
              </DialogDescription>
            </DialogHeader>

            <div className="px-6 py-5 space-y-4">
              {/* Selector de motivo principal */}
              <Select
                value={selectedMotive?.id ?? ""}
                onValueChange={(id) => {
                  const motive = primaryMotives.find((m) => m.id === id) ?? null;
                  setSelectedMotive(motive);
                  setSecondaryMotive(null);
                  setNote("");
                }}
              >
                <SelectTrigger className="w-full rounded-full border-primary/30 bg-white text-primary placeholder:text-primary/60 focus:ring-primary/30">
                  <SelectValue placeholder="Seleccionar motivo" />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {primaryMotives.map((m) => (
                    <SelectItem key={m.id} value={m.id} className="rounded-lg">
                      {m.description || m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Selector secundario para caso condicional */}
              {isConditional && (
                <>
                  <p className="text-xs text-muted-foreground">
                    {selectedMotive?.name === 'PSYCHOLOGIST_ASKED'
                      ? '¿Por qué pidió cancelar la psicóloga?'
                      : '¿Por qué pidió cancelar el paciente?'}
                  </p>
                  <Select
                    value={secondaryMotive?.id ?? ""}
                    onValueChange={(id) => {
                      const motive = secondaryMotives.find((m) => m.id === id) ?? null;
                      setSecondaryMotive(motive);
                      setNote("");
                    }}
                  >
                    <SelectTrigger className="w-full rounded-full border-primary/30 bg-white text-primary placeholder:text-primary/60 focus:ring-primary/30">
                      <SelectValue placeholder="Seleccionar motivo" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {secondaryMotives.map((m) => (
                        <SelectItem key={m.id} value={m.id} className="rounded-lg">
                          {m.description || m.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}

              {/* Nota obligatoria */}
              {(isConditional ? !!secondaryMotive : !!selectedMotive) && (
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Agregá un comentario (obligatorio)"
                  className="min-h-[100px] resize-none rounded-xl border-primary/30 bg-white focus-visible:ring-primary/30"
                />
              )}
            </div>

            <div className="flex gap-3 px-6 pb-6">
              <Button
                variant="outline"
                className="flex-1 rounded-full border-border hover:bg-muted/50"
                onClick={() => setStep(1)}
              >
                Atrás
              </Button>
              <Button
                className="flex-1 rounded-full bg-primary hover:bg-primary/90"
                onClick={handleConfirm}
                disabled={!canConfirm}
              >
                Confirmar
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
